import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowRight, Flame, Footprints, Moon, HeartHandshake, Loader2, Video, AlertCircle, Compass } from "lucide-react";
import BrandHeader from "../components/BrandHeader";
import { getAudit, getVideoSetting, pdfDownloadUrl, uploadAuditPdf } from "../lib/api";
import { pdfBlob as generatePdfBlob } from "../lib/pdf";
import { resolveMindsetBlocks } from "../data/mindset";

function parseVideoSrc(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&playsinline=1&rel=0` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1&playsinline=1` };
  return { kind: "video", src: url };
}

export default function Results() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [downloading, setDownloading] = useState(false);

  // The PDF blob is prepared as soon as the roadmap loads. We try the
  // server-stored copy first (the same one Justine downloads from the coach
  // dashboard) and fall back to generating it client-side. Either way, by the
  // time the user clicks the button the blob is sitting in memory and the
  // download is instantaneous, with zero dependence on network availability,
  // 404 races, or any browser policy that breaks <a download>.
  const [pdf, setPdf] = useState(null);        // Blob | null
  const [pdfState, setPdfState] = useState("loading"); // 'loading' | 'ready' | 'error'

  useEffect(() => {
    getAudit(id).then(setData).catch((e) => setErr(e.message || "Failed to load"));
    getVideoSetting()
      .then((s) => {
        if (s?.source === "upload" && s?.file_path) {
          setVideoUrl(`${process.env.REACT_APP_BACKEND_URL}${s.file_path}`);
        } else {
          setVideoUrl(s?.url || "");
        }
      })
      .catch(() => {});
  }, [id]);

  // Prepare the PDF blob once audit data is loaded.
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    (async () => {
      // 1) Try the stored copy first.
      try {
        const res = await fetch(pdfDownloadUrl(data.id), { credentials: "include" });
        if (cancelled) return;
        if (res.ok) {
          const blob = await res.blob();
          if (!cancelled) {
            setPdf(blob);
            setPdfState("ready");
          }
          return;
        }
        // fall through to local generation on any non-OK status (404 when the
        // upload from Audit.jsx didn't complete, 502 if storage is hiccupping)
      } catch (_e) {
        // Network error — fall through to local generation.
      }
      if (cancelled) return;
      // 2) Generate locally as a guaranteed fallback.
      try {
        const blob = await generatePdfBlob(data);
        if (cancelled) return;
        setPdf(blob);
        setPdfState("ready");
        // Best-effort: upload so the coach dashboard has the same file.
        try { await uploadAuditPdf(data.id, blob); } catch (_e) { /* non-fatal */ }
      } catch (e) {
        if (!cancelled) {
          console.error("Local PDF generation failed:", e);
          setPdfState("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [data]);

  if (err) return <ErrorState message={err} />;
  if (!data) return <LoadingState />;

  const name = data.answers?.name || "Beautiful";
  const video = parseVideoSrc(videoUrl);
  // Live view = current effective Roadmap (coach overrides applied); `data` stays original for the PDF snapshot.
  const view = data.effective ? { ...data, ...data.effective } : data;

  const handleDownload = () => {
    if (downloading) return;
    // If generation errored, allow a retry by re-triggering the effect.
    if (pdfState === "error") {
      setPdfState("loading");
      // Force the effect to re-run by toggling data via a no-op update.
      setData((d) => (d ? { ...d } : d));
      return;
    }
    if (pdfState !== "ready" || !pdf) return; // button is disabled in this case anyway
    setDownloading(true);
    try {
      const filename = `her-aligned-roadmap-${(name || "you").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      const blobUrl = URL.createObjectURL(pdf);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        setDownloading(false);
      }, 1500);
    } catch (e) {
      console.error("download failed", e);
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-base">
      <BrandHeader small />

      <section className="relative px-6 md:px-12 lg:px-16 pt-6 pb-12 max-w-6xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl border border-brand-border bg-gradient-to-br from-[#FBF5EC] via-[#F9F8F6] to-[#FBF5EC]">
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-brand-primary/20 to-transparent rounded-bl-[100%] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-brand-primary/10 to-transparent rounded-tr-[100%] pointer-events-none" />
          <div className="relative px-6 md:px-14 py-14 md:py-20 text-center md:text-left">
            <div className="label-eyebrow" data-testid="results-eyebrow">Your Personalised Roadmap</div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-display text-4xl sm:text-5xl lg:text-[56px] text-brand-ink mt-5 leading-[1.05]"
              data-testid="results-greeting"
            >
              Congratulations! Here&apos;s your <span className="italic text-brand-primary">Personalised Roadmap, {name}</span>!!
            </motion.h1>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-8" data-testid="results-video-slot"
        >
          {video ? (
            <div className="rounded-3xl overflow-hidden border border-brand-border bg-black aspect-video shadow-[0_8px_32px_rgba(43,43,43,0.08)]" data-testid="results-video">
              {video.kind === "iframe" ? (
                <iframe src={video.src} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title="A personal message from Justine" />
              ) : (
                <video src={video.src} className="w-full h-full object-cover" autoPlay muted playsInline controls />
              )}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-brand-primary/40 bg-[#FBF5EC]/50 aspect-video flex items-center justify-center text-center px-6" data-testid="results-video-placeholder">
              <div>
                <Video className="mx-auto text-brand-primary" size={40} strokeWidth={1.3} />
                <p className="mt-4 font-serif text-2xl text-brand-ink">A welcome video from Justine will sit here</p>
                <p className="mt-2 text-sm text-brand-ink-muted max-w-md mx-auto">Justine can add her personal welcome video from <span className="italic">Coach view</span>. Until then, the rest of your roadmap is ready below.</p>
              </div>
            </div>
          )}
        </motion.div>

        <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3 justify-center md:justify-start">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || pdfState === "loading"}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
            data-testid="download-pdf-btn"
            aria-live="polite"
          >
            {downloading ? (
              <>
                <Loader2 size={18} strokeWidth={1.8} className="animate-spin" /> Starting download…
              </>
            ) : pdfState === "loading" ? (
              <>
                <Loader2 size={18} strokeWidth={1.8} className="animate-spin" /> Preparing your PDF…
              </>
            ) : pdfState === "error" ? (
              <>
                <AlertCircle size={18} strokeWidth={1.8} /> Try preparing your PDF again
              </>
            ) : (
              <>
                <Download size={18} strokeWidth={1.8} /> Download a PDF of your Roadmap Here
              </>
            )}
          </button>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 pb-16 max-w-6xl mx-auto space-y-10 md:space-y-14">
        <CategoryBlock number="01" icon={<Flame size={22} strokeWidth={1.5} className="text-brand-primary" />} title="Your Nutrition Targets" testid="category-nutrition">
          <TargetGrid items={[
            { label: "Starting Calorie Range", value: view.calorie_range, testid: "calorie-range" },
            { label: "Starting Protein Range", value: view.protein_range, testid: "protein-range" },
          ]} />
          <Advice text={view.nutrition_advice} testid="nutrition-advice" />
        </CategoryBlock>

        <CategoryBlock number="02" icon={<Footprints size={22} strokeWidth={1.5} className="text-brand-primary" />} title="Your Fitness Targets" testid="category-fitness">
          <TargetGrid items={[
            { label: "Daily Step Range", value: view.step_range, testid: "step-range" },
            { label: "Training Frequency", value: view.training_frequency, testid: "training-frequency", small: true },
          ]} />
          <Advice text={view.fitness_advice} testid="fitness-advice" />
        </CategoryBlock>

        <CategoryBlock number="03" icon={<HeartHandshake size={22} strokeWidth={1.5} className="text-brand-primary" />} title="Your Wellbeing" testid="category-wellbeing">
          <HabitColumn icon={<Moon size={18} strokeWidth={1.5} />} label="Sleep habits to focus on" habits={view.sleep_habits} testid="sleep-habits" />
          <HabitColumn icon={<HeartHandshake size={18} strokeWidth={1.5} />} label="Stress regulation habits" habits={view.stress_habits} testid="stress-habits" />
        </CategoryBlock>

        <MindsetBlock patterns={data.answers?.mindset_patterns} root={data.answers?.mindset_root} guidance={view.mindset_guidance} />
      </section>

      <section className="px-6 md:px-12 lg:px-16 pb-24 max-w-6xl mx-auto">
        <div className="surface-card p-8 md:p-12" data-testid="next-steps">
          <div className="label-eyebrow">Your Onboarding Continues</div>
          <h2 className="font-display text-3xl md:text-4xl text-brand-ink mt-3">Your Next Steps</h2>
          <ol className="mt-8 space-y-6">
            <NextStep n="1"><strong>SAVE:</strong> Save your Roadmap by clicking the download button at the top of this page. A copy has been sent to Justine, so if you have any questions about your roadmap, reach out and ask her in our community or in your private 1:1 message channel on Everfit.</NextStep>
            <NextStep n="2">
              <span><strong>EVERFIT:</strong> To finish your onboarding, use the link below to download Everfit. It&apos;s the only app you will need on our journey together.</span>
              <br />
              <a
                href="https://invite.everfit.io/DNEE23SOJ4"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="everfit-invite-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3 text-white font-medium text-base hover:bg-brand-primary/90 transition-colors shadow-sm"
              >
                Download Everfit &amp; Join Here
                <ArrowRight size={18} strokeWidth={2} />
              </a>
            </NextStep>
            <NextStep n="3">Once you&apos;re in Everfit, you can upload your starting photos &amp; metrics and introduce yourself to our community so we can give you the warmest WELCOME <span aria-hidden="true">😉</span></NextStep>
          </ol>
          <p className="mt-10 font-serif italic text-xl md:text-2xl text-brand-ink" data-testid="next-steps-signoff">
            See you soon!<br/>
            Coach Justine x
          </p>
        </div>
      </section>
    </div>
  );
}

function NextStep({ n, children }) {
  return (
    <li className="flex gap-5">
      <div className="font-display text-3xl text-brand-primary leading-none shrink-0 w-10">{n}.</div>
      <p className="text-brand-ink text-base md:text-lg leading-relaxed">{children}</p>
    </li>
  );
}
function CategoryBlock({ number, icon, title, children, testid }) {
  return (
    <motion.section initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="surface-card p-8 md:p-12" data-testid={testid}>
      <div className="flex items-start gap-6">
        <div className="font-display text-5xl text-brand-primary/40 leading-none">{number}</div>
        <div className="flex-1">
          <div className="flex items-center gap-3">{icon}<h2 className="font-serif text-3xl md:text-4xl text-brand-ink">{title}</h2></div>
          <div className="mt-8 space-y-8">{children}</div>
        </div>
      </div>
    </motion.section>
  );
}
function TargetGrid({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((it) => (
        <div key={it.label} className="rounded-2xl border border-brand-border bg-[#FBF5EC]/60 p-6" data-testid={it.testid}>
          <div className="label-eyebrow">{it.label}</div>
          <div className={`mt-3 font-sans font-semibold text-brand-ink ${it.small ? "text-xl leading-snug" : "text-3xl md:text-4xl tracking-tight"}`}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}
function Advice({ text, testid }) {
  return <p className="text-brand-ink leading-relaxed text-base md:text-lg whitespace-pre-line" data-testid={testid}>{text}</p>;
}
function HabitColumn({ icon, label, habits, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow text-brand-primary">{icon}{label}</div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {(habits || []).map((h, i) => (
          <div key={i} className="rounded-2xl border border-brand-border p-6 bg-white" data-testid={`${testid}-${i}`}>
            <div className="font-serif text-xl text-brand-ink leading-snug">{h.title}</div>
            <p className="mt-3 text-sm text-brand-ink-muted leading-relaxed">{h.explanation}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
export function MindsetBlock({ patterns, root, guidance, animate = true }) {
  const { patternBlocks, rootBlock } = resolveMindsetBlocks(patterns, root);
  // Gracefully hide the section for historical records that don't have
  // mindset selections stored.
  if (!patternBlocks.length && !rootBlock) return null;
  return (
    <motion.section
      initial={animate ? { opacity: 0, y: 16 } : false}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="surface-card p-8 md:p-12"
      data-testid="category-mindset"
    >
      <div className="flex items-start gap-6">
        <div className="font-display text-5xl text-brand-primary/40 leading-none">04</div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Compass size={22} strokeWidth={1.5} className="text-brand-primary" />
            <h2 className="font-serif text-3xl md:text-4xl text-brand-ink">Your Mindset Roadmap</h2>
          </div>
          <div className="mt-8 space-y-8">
            {guidance ? <Advice text={guidance} testid="mindset-guidance" /> : null}
            {patternBlocks.map((b, i) => (
              <MindsetGuidanceBlock key={i} block={b} testid={`mindset-pattern-${i}`} />
            ))}
            {rootBlock && (
              <div className="rounded-2xl border border-brand-primary/30 bg-[#FBF5EC]/60 p-6 md:p-8" data-testid="mindset-root">
                <MindsetGuidanceBlock block={rootBlock} inline />
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
function MindsetGuidanceBlock({ block, testid, inline }) {
  return (
    <div data-testid={testid}>
      <h3 className={`font-serif ${inline ? "text-2xl md:text-3xl" : "text-2xl md:text-3xl"} text-brand-ink leading-snug`}>{block.title}</h3>
      <div className="mt-4 space-y-4">
        {(block.paragraphs || []).map((p, i) => (
          <p key={`p-${i}`} className="text-brand-ink leading-relaxed text-base md:text-lg whitespace-pre-line">{p}</p>
        ))}
        {block.bullets && (
          <ul className="mt-2 space-y-2 pl-1">
            {block.bullets.map((b, i) => (
              <li key={`b-${i}`} className="flex gap-3 text-brand-ink leading-relaxed text-base md:text-lg">
                <span className="text-brand-primary mt-1">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {(block.outroParagraphs || []).map((p, i) => (
          <p key={`o-${i}`} className="text-brand-ink leading-relaxed text-base md:text-lg whitespace-pre-line">{p}</p>
        ))}
      </div>
    </div>
  );
}
function LoadingState() {
  return (
    <div className="min-h-screen bg-brand-base flex flex-col items-center justify-center" data-testid="results-loading">
      <Loader2 className="text-brand-primary animate-spin" size={36} strokeWidth={1.5} />
      <p className="mt-6 font-serif text-2xl text-brand-ink">Pulling your roadmap…</p>
    </div>
  );
}
function ErrorState({ message }) {
  return (
    <div className="min-h-screen bg-brand-base flex flex-col items-center justify-center px-6 text-center">
      <h2 className="font-serif text-3xl text-brand-ink">We couldn&apos;t find that roadmap.</h2>
      <p className="mt-3 text-brand-ink-muted">{message}</p>
      <Link to="/" className="btn-primary mt-8">Start a new roadmap</Link>
    </div>
  );
}
